import React, { useState, useEffect, useRef } from 'react';
import { Trash2, X } from 'lucide-react';

interface Project {
  id: string;
  name: string;
  directory_path?: string;
}

interface DeleteProjectModalProps {
  isOpen: boolean;
  project: Project | null;
  onClose: () => void;
  onConfirm: (projectId: string) => Promise<void>;
  isDeleting?: boolean;
}

const DeleteProjectModal: React.FC<DeleteProjectModalProps> = ({
  isOpen,
  project,
  onClose,
  onConfirm,
  isDeleting = false
}) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteInputRef = useRef<HTMLInputElement>(null);

  // Reset confirmation text when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setDeleteConfirmText('');
      // Focus input when modal opens
      setTimeout(() => {
        if (deleteInputRef.current) {
          deleteInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleConfirm = async () => {
    if (!project || deleteConfirmText !== project.name || isDeleting) {
      return;
    }
    await onConfirm(project.id);
  };

  const isDeleteDisabled = !project || deleteConfirmText !== project.name || isDeleting;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-neutral-200 dark:border-neutral-800 animate-in zoom-in-95 slide-in-from-bottom-2 duration-300">
        <div className="flex items-start mb-6">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/20 dark:to-orange-900/20 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Delete Project
            </h3>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              Are you sure you want to delete <span className="font-semibold text-neutral-900 dark:text-white">{project?.name}</span>?
              This project will be marked for deletion and permanently removed after 30 days.
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 ml-4 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-sm text-neutral-700 dark:text-neutral-300 mb-2">
            To confirm, please type <span className="font-mono font-semibold bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded text-red-600 dark:text-red-400">{project?.name}</span>
          </label>
          <input
            ref={deleteInputRef}
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type project name to confirm"
            className="w-full px-4 py-2.5 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
            disabled={isDeleting}
            autoComplete="off"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1 px-4 py-2.5 rounded-lg font-medium transition-all bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isDeleteDisabled}
            className={`flex-1 px-4 py-2.5 rounded-lg text-white font-medium transition-all transform ${!isDeleteDisabled
              ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98]'
              : 'bg-neutral-300 dark:bg-neutral-700 cursor-not-allowed opacity-50'
              }`}
          >
            {isDeleting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Deleting...
              </span>
            ) : (
              'Delete project'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteProjectModal;
